import { SecurityAccessFeaturesEnum } from 'bff';
import { atom, createStore } from 'jotai/vanilla';
import { useAtom } from 'jotai/react';
import { canAccessAll, canAccessAny } from './can-access';
import {
  getActingAsIntermediaryFromStorage,
  storeActingAsIntermediary
} from './acting-as-user-storage';
import { UserWithFeatures } from 'types/auth/user-context.type';

interface UserContextManagerApiType {
  getCurrentAuthenticatedUserWithFeatures: () => UserWithFeatures | undefined;
  getActingAsIntermediaryUser: () => UserWithFeatures | undefined;
  setCurrentAuthenticatedUserWithFeatures: (actingAsUser: UserWithFeatures | undefined) => void;
  setActingAsIntermediaryUser: (actingAsUser: UserWithFeatures | undefined) => void;
  getUserFeatures: () => SecurityAccessFeaturesEnum[];
  canAccessAll: (features: SecurityAccessFeaturesEnum | SecurityAccessFeaturesEnum[]) => boolean;
  canAccessAny: (features: SecurityAccessFeaturesEnum | SecurityAccessFeaturesEnum[]) => boolean;
}

const currentUserAtom = atom<UserWithFeatures | undefined>(undefined);
const currentActingAsIntermediaryAtom = atom<UserWithFeatures | undefined>(
  getActingAsIntermediaryFromStorage()
);

const store = createStore();

export const useUserContext = () => {
  const [currentIntermediary, setCurrentIntermediary] = useAtom(currentUserAtom, { store });
  const [currentActingAsIntermediary, setCurrentActingAsIntermediary] = useAtom(
    currentActingAsIntermediaryAtom,
    { store }
  );
  const getFeatures = () => {
    // For AF we do not replace the current intermediary's features with the one they are acting as, therefore we return the acting as intermediary, but not their features.
    // Eg. Principal acting as someone else, keeps Principal features and does not inherit the other person's features.

    if (currentActingAsIntermediary && currentActingAsIntermediary.features.length > 0) {
      return currentActingAsIntermediary.features;
    }
    if (currentIntermediary) {
      return currentIntermediary.features;
    }
    return [];
  };

  const api: UserContextManagerApiType = {
    getCurrentAuthenticatedUserWithFeatures: () => currentIntermediary,
    getActingAsIntermediaryUser: () => currentActingAsIntermediary,
    getUserFeatures: getFeatures,
    setCurrentAuthenticatedUserWithFeatures: (user: UserWithFeatures | undefined) => {
      setCurrentIntermediary(user);
    },
    setActingAsIntermediaryUser: (intermediaryUser: UserWithFeatures | undefined) => {
      storeActingAsIntermediary(intermediaryUser);
      setCurrentActingAsIntermediary(intermediaryUser);
    },
    canAccessAll: (features: SecurityAccessFeaturesEnum | SecurityAccessFeaturesEnum[]) => {
      const userFeatures = getFeatures();
      return canAccessAll(features, userFeatures);
    },
    canAccessAny: (features: SecurityAccessFeaturesEnum | SecurityAccessFeaturesEnum[]) => {
      const userFeatures = getFeatures();
      return canAccessAny(features, userFeatures);
    }
  };

  return api;
};
